
import { Options, mixins } from "vue-class-component";
import {
  AlbumAndAssetManagementMixin,
  RoutingUtilityMixin,
} from "@/mixins";
import {
  AssetsInterface,
  AssetType,
  Direction,
  FoldersWithAssetsInterface,
} from "@/types";
import { hasAccessToAsset } from "@/utils/assetAccessChecker";
import { assetsModule, usersModule } from "@/store";
import VideoComponent from "./VideoComponent/index.vue";
import ImageComponent from "./ImageComponent/index.vue";
import Model3DComponent from "./Model3DComponent/index.vue";
import AccessDeniedComponent from "./AccessDenied/index.vue";
import ToggleAssetPinButton from "./ToggleAssetPinButton/index.vue";
import AssetDescription from "./AssetDescription/index.vue";
import SliderArrow from "./SliderArrow/index.vue";
import { useMq } from "vue3-mq";

@Options({
  name: "Slider",
  components: {
    SliderArrow,
    AccessDeniedComponent,
    ToggleAssetPinButton,
    VideoComponent,
    ImageComponent,
    Model3DComponent,
    AssetDescription,
  },
  watch: {
    showAccessDeniedComponent(val) {
      if (val) {
        this.emitter.emit("asset-description-toggle", false);
      }
    }
  }
})
export default class Slider extends mixins(
  AlbumAndAssetManagementMixin,
  RoutingUtilityMixin,
) {
  $refs!: {
    slider: HTMLFormElement;
  };
  protected hasAccessToAsset = hasAccessToAsset;
  protected direction = Direction;
  protected isAssetDescriptionVisible = false;
  private mq = useMq();

  protected get isLoadingUserAccess(): boolean {
    return usersModule.isLoading;
  }

  protected get dataLoadingInProgress(): boolean {
    return assetsModule.getAllDataLoader;
  }

  protected get getFilteredAlbums(): FoldersWithAssetsInterface[] {
    return assetsModule.getFilteredAlbums;
  }

  protected get getDefaultAlbums(): FoldersWithAssetsInterface[] {
    return assetsModule.getFoldersWithAssets;
  }

  protected get isDesktop(): boolean {
    return ["lg", "xl", "xxl"].includes(this.mq.current);
  }

  protected get getCurrentAlbums(): FoldersWithAssetsInterface[] {
    return this.isDesktop ? this.getFilteredAlbums : this.getDefaultAlbums;
  }

  protected get getAssetsData(): AssetsInterface[] {
    return this.extractNestedAssets(this.getCurrentAlbums);
  }

  protected get getCurrentAsset(): AssetsInterface | undefined {
    return this.getAssetsData.find(
      (a) => a.slug === this.$route.params.assetSlug
    );
  }

  protected get getAsset(): AssetsInterface | undefined {
    return this.getCurrentAsset;
  }

  protected get isRouteValid(): boolean {
    const { assetSlug } = this.$route.params;
    const albums = this.getCurrentAlbums;
    const asset = this.findAssetBySlug(albums, assetSlug as string);

    return this.isRoutePathValid(this.$route.path, asset);
  }

  protected get isImage(): AssetType {
    return AssetType.Image;
  }

  protected get isModels(): AssetType {
    return AssetType.Models;
  }

  protected get isVideo(): AssetType {
    return AssetType.Video;
  }

  protected get showAccessDeniedComponent(): boolean {
    return (
      !hasAccessToAsset(this.getAsset) &&
      !this.isLoadingUserAccess
    );
  }

  private handleArrowKeyPress(event: KeyboardEvent) {
    if (event.key === "ArrowUp") {
      this.moveIndex(-1);
    } else if (event.key === "ArrowDown") {
      this.moveIndex(1);
    }
  }

  private moveIndex(direction: number) {
    const assetIndex = this.getAssetsData.findIndex(
      (a) => a.slug === this.$route.params.assetSlug
    );
    const newIndex = assetIndex + direction;

    if (newIndex >= 0 && newIndex < this.getAssetsData.length) {
      const asset = this.getAssetsData[newIndex];
      const newPath = this.getFullAssetRouteAsString(asset);

      this.$router.push(newPath);
    }
  }

  private handleMessageEvent(event: MessageEvent): void {
    if (event.data?.action === "OnShowTutorial") {
      this.toggleTutorialVisibility(event.data?.showTutorial);
    }
  }

  private toggleTutorialVisibility(isHidden: boolean): void {
    const slider = this.$refs.slider;
    if (!slider) return;

    const pinAssetButton = slider.querySelector(".pin-asset-button");

    slider.querySelectorAll(".slider-arrow").forEach((arrow: Element) => {
      arrow.classList[isHidden ? "add" : "remove"]("is-hidden");
    });

    pinAssetButton?.classList[isHidden ? "add" : "remove"]("is-hidden");
  }

  private showAssetDescription(state: boolean): void {
    this.isAssetDescriptionVisible = state;
  }

  protected handleAssetDescriptionClose(): void {
    this.isAssetDescriptionVisible = false;
    this.emitter.emit("asset-description-toggle", false);
  }

  mounted(): void {
    window.addEventListener("keydown", this.handleArrowKeyPress);
    window.addEventListener("message", this.handleMessageEvent);

    this.emitter.on("asset-description-toggle", this.showAssetDescription);
  }

  beforeUnmount(): void {
    window.removeEventListener("keydown", this.handleArrowKeyPress);
    window.removeEventListener("message", this.handleMessageEvent);

    this.emitter.off("asset-description-toggle", this.showAssetDescription);
  }
}
